import React from 'react';
import cx from 'classnames';

/**
 * ContentWrapper: outermost container
 * NO usa Container base para evitar el override de max-width: 1400px
 */

type Alignment = 'left' | 'center' | 'right';

interface ContentWrapperProps {
  /** Child elements to render inside the wrapper */
  children?: React.ReactNode;
  /** Additional CSS classes */
  className?: string;
  /** HTML element type to render as */
  as?: React.ElementType;
  /** CSS color string used as CSS background-color property of the component's container */
  alignment?: Alignment;
  /** Unique identifier */
  bgColor?: string;
  /** CSS color string to be inherited by the component's children, typically for text or any foreground elements */
  fgColor?: string;
  /** ARIA label for accessibility */
  ariaLabel?: string;
  /** Top padding */
  paddingTop?: string;
  /** Bottom padding */
  paddingBottom?: string;
  /** Gap */
  gap?: string;
  /** Id */
  id?: string;
  /** Minimum height */
  minHeight?: string;
  /** Additional HTML attributes */
  style?: React.CSSProperties;
  [key: string]: any;
}

const ContentWrapper: React.FC<ContentWrapperProps> = ({
  children,
  className = '',
  as: Component = 'div',
  alignment = 'center',
  bgColor = 'var(--contentwrapper-variantdefault-statedefault-bgcolor)',
  fgColor = 'var(--contentwrapper-variantdefault-statedefault-fgcolor)',
  ariaLabel = 'Content Wrapper',
  paddingTop,
  paddingBottom,
  gap,
  id,
  minHeight,
  ...rest
}) => {
  const getAlignItems = (alignment) => {
    switch (alignment) {
      case 'left':
        return 'flex-start';
      case 'center':
        return 'center';
      case 'right':
        return 'flex-end';
      default:
        return 'center';
    }
  };

  const containerStyles: React.CSSProperties = {
    backgroundColor: bgColor,
    color: fgColor,
    paddingTop,
    paddingBottom,
    gap,
    display: 'flex',
    alignItems: getAlignItems(alignment),
    justifyContent: getAlignItems(alignment),
    minHeight,
    ...(rest.style || {}),
  };

  return (
    <Component
      className={cx('content-wrapper', className)}
      style={containerStyles}
      id={id}
      aria-label={ariaLabel || `Section title: ${id}`}
      role="section"
      {...rest}
    >
      {children}
    </Component>
  );
};

export default ContentWrapper;